import React, { useState } from "react";
import { toast } from "sonner";
import Drawer, { PanelSection, PanelInput } from "./Drawer";
import { projectsApi } from "@/lib/api";
import { DRIFT_TIERS } from "@/lib/drift";

const STATUSES = ["planning", "active", "completed", "archived"];
const TIMEZONES = [
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Madrid", "Europe/Amsterdam",
  "America/New_York", "America/Los_Angeles", "America/Chicago", "America/Denver",
  "Australia/Sydney", "Asia/Tokyo", "Asia/Singapore", "UTC",
];
const AI_VERBOSITY_OPTIONS = [
  { value: "minimal", label: "MINIMAL — placements + critical only" },
  { value: "standard", label: "STANDARD — full copilot (default)" },
  { value: "proactive", label: "PROACTIVE — surface every plausible suggestion" },
];

export default function ProjectPanel({ open, onClose, project, onMutated }) {
  const [draft, setDraft] = useState({
    name: project?.name || "",
    start_date: project?.start_date || "",
    end_date: project?.end_date || "",
    timezone: project?.timezone || "Europe/London",
    status: project?.status || "planning",
    notes: project?.notes || "",
    drift_small: project?.settings?.drift_thresholds?.small ?? DRIFT_TIERS.small,
    drift_medium: project?.settings?.drift_thresholds?.medium ?? DRIFT_TIERS.medium,
    drift_large: project?.settings?.drift_thresholds?.large ?? DRIFT_TIERS.large,
    ai_verbosity: project?.settings?.ai_verbosity || "standard",
  });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (project) {
      setDraft({
        name: project.name || "",
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        timezone: project.timezone || "Europe/London",
        status: project.status || "planning",
        notes: project.notes || "",
        drift_small: project.settings?.drift_thresholds?.small ?? DRIFT_TIERS.small,
        drift_medium: project.settings?.drift_thresholds?.medium ?? DRIFT_TIERS.medium,
        drift_large: project.settings?.drift_thresholds?.large ?? DRIFT_TIERS.large,
        ai_verbosity: project.settings?.ai_verbosity || "standard",
      });
    }
  }, [project, open]);

  const save = async () => {
    setBusy(true);
    try {
      await projectsApi.update(project.id, {
        name: draft.name,
        start_date: draft.start_date || null,
        end_date: draft.end_date || null,
        timezone: draft.timezone,
        status: draft.status,
        notes: draft.notes || null,
        settings: {
          ...(project.settings || {}),
          drift_thresholds: {
            small: +draft.drift_small || 15,
            medium: +draft.drift_medium || 45,
            large: +draft.drift_large || 60,
          },
          ai_verbosity: draft.ai_verbosity,
        },
      });
      onMutated && (await onMutated());
      toast.success("Project saved");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!project) return null;

  return (
    <Drawer open={open} onClose={onClose} title="PROJECT SETTINGS" subtitle={project.name} dataTestid="project-panel">
      <PanelSection title="GENERAL">
        <PanelInput label="NAME" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} dataTestid="project-name-input" />
        <div className="grid grid-cols-2 gap-2">
          <PanelInput label="START DATE" type="date" value={draft.start_date} onChange={(v) => setDraft({ ...draft, start_date: v })} />
          <PanelInput label="END DATE" type="date" value={draft.end_date} onChange={(v) => setDraft({ ...draft, end_date: v })} />
        </div>
        <PanelInput label="TIMEZONE" value={draft.timezone} onChange={(v) => setDraft({ ...draft, timezone: v })} options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))} />
        <PanelInput label="STATUS" value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={STATUSES.map((s) => ({ value: s, label: s.toUpperCase() }))} />
        <PanelInput label="NOTES" value={draft.notes} onChange={(v) => setDraft({ ...draft, notes: v })} />
      </PanelSection>

      <PanelSection title="DRIFT THRESHOLDS (MIN)">
        <div className="grid grid-cols-3 gap-2">
          <PanelInput label="SMALL" type="number" value={draft.drift_small} onChange={(v) => setDraft({ ...draft, drift_small: v })} />
          <PanelInput label="MEDIUM" type="number" value={draft.drift_medium} onChange={(v) => setDraft({ ...draft, drift_medium: v })} />
          <PanelInput label="LARGE" type="number" value={draft.drift_large} onChange={(v) => setDraft({ ...draft, drift_large: v })} />
        </div>
        <p className="mono text-2xs text-ink-mute uppercase tracking-wider mt-1">
          Tiered surfacing: ≤small = inline chip · ≤medium = toast strip · &gt;medium = persistent banner.
        </p>
      </PanelSection>

      <PanelSection title="AI VERBOSITY">
        <PanelInput
          label="LEVEL"
          value={draft.ai_verbosity}
          onChange={(v) => setDraft({ ...draft, ai_verbosity: v })}
          options={AI_VERBOSITY_OPTIONS}
        />
      </PanelSection>

      <div className="p-3 sticky bottom-0 bg-bg-surface border-t border-line">
        <button onClick={save} disabled={busy} className="btn-primary w-full justify-center" data-testid="project-save-btn">
          SAVE
        </button>
      </div>
    </Drawer>
  );
}
